import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import {
  getSharedQuote,
  respondToSharedQuote,
  sharedQuoteQueryKey,
} from '@/lib/api/public-quotes';

import { SharedQuoteView } from './shared-quote-view';

// Public, unauthenticated page for `/q/:token`. Renders outside the auth guard
// and app shell — no nav, no login redirect, no authed fetches. The token is
// capability-bearing, so it is never logged or sent anywhere but the API.
export function PublicQuote() {
  const { t } = useLanguage();
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: sharedQuoteQueryKey(token ?? ''),
    queryFn: () => getSharedQuote(token!),
    enabled: Boolean(token),
    retry: false,
  });
  const { refetch } = query;

  // Guard the manual retry so a frustrated tap can't hammer the rate limiter.
  const [cooling, setCooling] = useState(false);
  const coolTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retry = useCallback(() => {
    setCooling(true);
    if (coolTimerRef.current) clearTimeout(coolTimerRef.current);
    coolTimerRef.current = setTimeout(() => setCooling(false), 3000);
    void refetch();
  }, [refetch]);

  // Accept / decline over the signed link (no auth). The response returns the
  // refreshed shared view, which we drop straight into the cache.
  const [responderName, setResponderName] = useState('');
  const [responded, setResponded] = useState<'accepted' | 'declined' | null>(
    null,
  );
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const declineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const respondMutation = useMutation({
    mutationFn: (nextStatus: 'accepted' | 'declined') =>
      respondToSharedQuote(token!, {
        status: nextStatus,
        responder_name: responderName.trim() || undefined,
      }),
    onSuccess: (data, nextStatus) => {
      queryClient.setQueryData(sharedQuoteQueryKey(token ?? ''), data);
      setResponded(nextStatus);
      toast.success(
        nextStatus === 'accepted'
          ? t.sharedQuoteAcceptedMsg
          : t.sharedQuoteDeclinedMsg,
      );
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.error(t.sharedQuoteAlreadyResponded);
        void queryClient.invalidateQueries({
          queryKey: sharedQuoteQueryKey(token ?? ''),
        });
        return;
      }
      toast.error(t.sharedQuoteRespondError);
    },
  });

  function requestDecline() {
    if (declineTimerRef.current) clearTimeout(declineTimerRef.current);
    if (confirmingDecline) {
      setConfirmingDecline(false);
      respondMutation.mutate('declined');
      return;
    }
    setConfirmingDecline(true);
    declineTimerRef.current = setTimeout(
      () => setConfirmingDecline(false),
      4000,
    );
  }

  useEffect(() => {
    return () => {
      if (coolTimerRef.current) clearTimeout(coolTimerRef.current);
      if (declineTimerRef.current) clearTimeout(declineTimerRef.current);
    };
  }, []);

  const errorStatus = isAxiosError(query.error)
    ? query.error.response?.status
    : undefined;

  let content: React.ReactNode;
  if (query.isPending) {
    content = <QuoteSkeleton />;
  } else if (query.isSuccess) {
    const quote = query.data;
    content = (
      <div className="flex flex-col gap-4">
        <SharedQuoteView quote={quote} />

        {quote.status === 'pending' && (
          <div
            className="flex flex-col gap-3 rounded-lg border bg-background p-5"
            data-testid="shared-quote-actions"
          >
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">{t.sharedQuoteResponderName}</span>
              <input
                type="text"
                value={responderName}
                maxLength={200}
                onChange={(e) => setResponderName(e.target.value)}
                data-testid="shared-quote-responder-name"
                className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={respondMutation.isPending}
                onClick={() => respondMutation.mutate('accepted')}
                data-testid="shared-quote-accept"
              >
                {t.sharedQuoteAccept}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={respondMutation.isPending}
                onClick={requestDecline}
                data-testid="shared-quote-decline"
              >
                {confirmingDecline
                  ? t.sharedQuoteDeclineConfirm
                  : t.sharedQuoteDecline}
              </Button>
            </div>
          </div>
        )}

        {quote.status !== 'pending' && responded && (
          <p
            className="text-sm text-center text-muted-foreground"
            data-testid="shared-quote-responded"
          >
            {responded === 'accepted'
              ? t.sharedQuoteAcceptedMsg
              : t.sharedQuoteDeclinedMsg}
          </p>
        )}
      </div>
    );
  } else if (errorStatus === 404) {
    content = <Message text={t.sharedQuoteInvalid} />;
  } else if (errorStatus === 429) {
    content = (
      <Message
        text={t.sharedQuoteRateLimited}
        onRetry={retry}
        retryDisabled={cooling || query.isFetching}
      />
    );
  } else {
    content = (
      <Message
        text={t.sharedQuoteError}
        onRetry={retry}
        retryDisabled={query.isFetching}
      />
    );
  }

  return (
    <div className="min-h-svh bg-muted/30 px-4 py-10 flex items-start justify-center">
      <div className="w-full max-w-lg">{content}</div>
    </div>
  );
}

function Message({
  text,
  onRetry,
  retryDisabled,
}: {
  text: string;
  onRetry?: () => void;
  retryDisabled?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div
      className="rounded-lg border bg-background p-8 flex flex-col items-center gap-4 text-center"
      data-testid="shared-quote-message"
    >
      <p className="text-sm text-muted-foreground">{text}</p>
      {onRetry && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={retryDisabled}
          onClick={onRetry}
          data-testid="shared-quote-retry"
        >
          {t.sharedQuoteRetry}
        </Button>
      )}
    </div>
  );
}

function QuoteSkeleton() {
  return (
    <div
      className="rounded-lg border bg-background p-5 flex flex-col gap-5 animate-pulse"
      data-testid="shared-quote-skeleton"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-2 w-1/2">
          <div className="h-5 rounded bg-muted" />
          <div className="h-4 rounded bg-muted w-3/4" />
        </div>
        <div className="h-5 w-16 rounded-full bg-muted" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 rounded bg-muted w-2/3" />
      </div>
      <div className="h-6 rounded bg-muted w-1/3 self-end" />
    </div>
  );
}
